import apiClient from "./api";

/**
 * Authentication Service
 * Handles all API calls related to authentication
 * Now uses axios with automatic token refresh
 */
class AuthService {
  /**
   * Handle axios error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const data = error.response.data;

      if (error.response.status === 400 && data.errors) {
        // Validation errors
        const errorMessage = data.errors.map((err) => err.msg).join(", ");
        throw new Error(errorMessage);
      }

      throw new Error(data.message || "An error occurred");
    } else if (error.request) {
      // Request made but no response
      throw new Error(
        "Cannot connect to the server. Please ensure the backend is running."
      );
    } else {
      // Something else happened
      throw new Error(error.message || "An error occurred");
    }
  }

  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} User data and token
   */
  async login(email, password) {
    try {
      const response = await apiClient.post("/api/auth/login", {
        email,
        password,
      });

      const data = response.data;

      if (data.success && data.data) {
        return {
          success: true,
          user: data.data.user,
          tokens: data.data.tokens,
        };
      }

      throw new Error("Invalid response format");
    } catch (error) {
      console.error("Login error:", error);
      this.handleError(error);
    }
  }

  /**
   * Register new user
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} firstName - User first name
   * @param {string} lastName - User last name
   * @param {string} role - User role (optional)
   * @returns {Promise<Object>} User data and token
   */
  async register(email, password, firstName, lastName, role = "user") {
    try {
      const response = await apiClient.post("/api/auth/register", {
        email,
        password,
        firstName,
        lastName,
        role,
      });

      const data = response.data;

      if (data.success && data.data) {
        return {
          success: true,
          user: data.data.user,
          tokens: data.data.tokens,
        };
      }

      throw new Error("Invalid response format");
    } catch (error) {
      console.error("Register error:", error);
      this.handleError(error);
    }
  }

  /**
   * Logout user
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      await apiClient.post("/api/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
      // Even if the API call fails, we should still clear local storage
    } finally {
      localStorage.removeItem("cia_user");
      localStorage.removeItem("cia_token");
      localStorage.removeItem("cia_refresh_token");
    }
  }

  /**
   * Get current user profile
   * @returns {Promise<Object>} User data
   */
  async getCurrentUser() {
    try {
      const response = await apiClient.get("/api/auth/me");
      const data = response.data;

      if (data.success && data.data) {
        return data.data.user;
      }

      throw new Error("Invalid response format");
    } catch (error) {
      console.error("Get current user error:", error);
      this.handleError(error);
    }
  }

  /**
   * Refresh access token
   * NOTE: This is now handled automatically by the axios interceptor
   * You don't need to call this manually anymore
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<Object>} New tokens
   */
  async refreshToken(refreshToken) {
    try {
      const response = await apiClient.post("/api/auth/refresh", {
        refreshToken,
      });

      const data = response.data;

      if (data.success && data.data) {
        return data.data.tokens;
      }

      throw new Error("Invalid response format");
    } catch (error) {
      console.error("Refresh token error:", error);
      this.handleError(error);
    }
  }

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  async changePassword(currentPassword, newPassword) {
    try {
      await apiClient.post("/api/auth/change-password", {
        currentPassword,
        newPassword,
      });
    } catch (error) {
      console.error("Change password error:", error);
      this.handleError(error);
    }
  }
}

// Export a singleton instance
const authService = new AuthService();
export default authService;
